import { DataLayerEvent } from "./events/DataLayerEvent";

export class DataLayerTransactionProductData {
  name: string;
  sku: string;
  price: number;
  quantity: number;

  constructor(name: string, sku: string, price: number, quantity: number) {
    this.name = name;
    this.sku = sku;
    this.price = price;
    this.quantity = quantity;
  }
}
export class DataLayerTransaction {
  transactionId: string;
  transactionProducts?: Array<DataLayerTransactionProductData>;
  transactionTotal?: number;

  constructor(transactionId: string) {
    this.transactionId = transactionId;
  }
}

// eslint-disable-next-line @typescript-eslint/no-namespace
export namespace BNGoogleTagManager {
  export const addEventToDataLayerAsync = async (event: DataLayerEvent, timeoutMs = 2000): Promise<void> => {
    const p = await new Promise<void>((resolve) => {
      if ((window as any).google_tag_manager === undefined) {
        BNGoogleTagManager.addEventToDataLayer(event);
        resolve();
      } else {
        let isResolved = false;
        BNGoogleTagManager.addEventToDataLayer(event, (id: string) => {
          if (id === undefined || id.startsWith("GTM")) {
            isResolved = true;
            resolve();
          }
        });
        setTimeout(() => {
          if (isResolved) return;
          isResolved = true;
          resolve();
        }, timeoutMs);
      }
    }).catch((err) => {
      throw err;
    });
    return p;
  };

  export const addCustomInfoToDataLayer = (key: string, value: any) => {
    if (typeof window !== "undefined") {
      const dataLayer = ((window as any).dataLayer = (window as any).dataLayer || []);
      if (dataLayer.length > 0) {
        dataLayer[0][key] = value;
      }
    }
  };

  export const addTransactionToDataLayer = (transaction: DataLayerTransaction): void => {
    if (typeof window !== "undefined") {
      const dataLayer = ((window as any).dataLayer = (window as any).dataLayer || []);
      dataLayer.push(transaction);
    }
  };

  export const addEventToDataLayer = (
    event: DataLayerEvent,
    callback?: (id: string, eventData: unknown[]) => void,
  ): void => {
    const eventToPush = event.toGaObject();

    if (callback !== null && eventToPush["GA4EventName"] !== "ui_element_click") {
      eventToPush["eventCallback"] = callback;
      eventToPush["eventTimeout"] = 2000;
    }

    if (event["category"] !== EventCategory.NotSet && typeof window !== "undefined") {
      const dataLayer = ((window as any).dataLayer = (window as any).dataLayer || []);
      dataLayer.push(eventToPush);
    }
  };

  export const getEventCategory = (url?: string): EventCategory => {
    if (typeof url === "undefined") {
      if (typeof window !== "undefined" && typeof window.location !== "undefined") {
        url = window.location.pathname;
      }
    }

    url = getPathFromUrl(url);
    url = trimTrailingSlash(url);

    let onTransactionPage = false;
    let lastUrlSegment = url ? getPathFromUrl(url)?.split("/").reverse()[0] : "";
    if (url && lastUrlSegment?.toLowerCase() === "transactions") {
      onTransactionPage = true;
      lastUrlSegment = url.split("/").reverse()[1];
    }
    switch (lastUrlSegment?.toLowerCase()) {
      case "minside":
      case "omatsivuni":
      case "minsida":
        return EventCategory.MyPage;
      case "creditcard":
        return onTransactionPage ? EventCategory.MyPageCreditCardTransactions : EventCategory.MyPageCreditCard;
      default:
        return EventCategory.NotSet;
    }
  };

  export const getEventLabel = (url?: string): string => {
    url = getPathFromUrl(url);
    url = trimTrailingSlash(url);
    return url ? url.split("/").reverse()[0] : "";
  };

  export const getNumberOfDaysFromCurrentDate = (fromDate: Date): number => {
    if (fromDate) {
      const difference = +fromDate - +new Date();
      const differenceInDays = Math.floor(difference / (24 * 60 * 60 * 1000));
      return differenceInDays + 1;
    } else {
      return 0;
    }
  };
}

export enum EventCategory {
  NotSet = "NotSet",
  MyPage = "MyPage",
  LoanApplication = "LoanApplication",
  GreenLoanApplication = "GreenLoanApplication",
  CreditPurchaseApplication = "CreditPurchaseApplication",
  TopUpApplication = "TopUpApplication",
  LoanExtensionApplication = "LoanExtensionApplication",
  LoanCustomPaymentApplication = "LoanCustomPaymentApplication",
  MyPageCreditCard = "MyPageCreditCard",
  MyPageCreditCardTransactions = "MyPageCreditCardTransactions",
  SavingsAccountApplication = "SavingsAccountApplication",
  SavingsAccountPlusApplication = "SavingsAccountPlusApplication",
  SavingsAccountDelayedWithdrawalApplication = "SavingsAccountDelayedWithdrawalApplication",
  SavingsAccountFixedTerm12Application = "SavingsAccountFixedTerm12Application",
  SavingsAccountFixedTerm24Application = "SavingsAccountFixedTerm24Application",
  CreditCardApplication = "CreditCardApplication",
  CreditCardLimitIncreaseApplication = "CreditCardLimitIncreaseApplication",
  Insurance = "Insurance",
  CreditLineApplication = "CreditLineApplication",
  CreditLineLimitIncreaseApplication = "CreditLineLimitIncreaseApplication",
  EquityReleaseApplication = "EquityReleaseApplication",
  RefinanceEditor = "RefinanceEditor",
  ViewPromotion = "ViewPromotion",
  ViewItem = "ViewItem",
  Purchase = "Purchase",
  SelectPromotion = "SelectPromotion",
  AddToCart = "AddToCart",
  RemoveFromCart = "RemoveFromCart",
}

function getPathFromUrl(url?: string): string | undefined {
  return url ? url.split(/[?#]/)[0] : url;
}

function trimTrailingSlash(url?: string): string | undefined {
  return url ? url.replace(/\/$/, "") : url;
}

export enum EventAction {
  PayoutDetails = "PayoutDetails",
  DebtSpecification = "DebtSpecification",
  Header = "Header",
  TopNavigation = "TopNavigation",
  SideNavigation = "SideNavigation",
  BottomNavigation = "BottomNavigation",
  TopMobileNavigation = "TopMobileNavigation",
  TopBanner = "TopBanner",
  ImportantInfo = "ImportantInfo",
  CreditCard = "CreditCard",
  CreditLine = "CreditLine",
  SavingsAccount = "SavingsAccount",
  SavingsAccountPlus = "SavingsAccountPlus",
  SavingsAccountDelayedWithdrawal = "SavingsAccountDelayedWithdrawal",
  Loan = "Loan",
  SalesFinance = "SalesFinance",
  CashPoints = "CashPoints",
  Insurance = "Insurance",
  ProductBanner = "ProductBanner",
  Other = "Other",
  CreditCardAction = "CreditCardAction",
  InvoiceAction = "InvoiceAction",
  ChoosePeriod = "ChoosePeriod",
  ToggleChoosePeriod = "ToggleChoosePeriod",
  ToggleFilter = "ToggleFilter",
  ToggleFilterCategory = "ToggleFilterCategory",
  TransactionClick = "TransactionClick",
  Download = "Download",
  ChooseDateFrom = "ChooseDateFrom",
  ChooseDateTo = "ChooseDateTo",
  TransactionSearchText = "TransactionSearchText",
  AmountFrom = "AmountFrom",
  AmountTo = "AmountTo",
  DocumentationConsent = "DocumentationConsent",
  DocumentationConsentSalary = "DocumentationConsentSalary",
  DocumentationConsentTax = "DocumentationConsentTax",
  DocumentationConsentAddress = "DocumentationConsentAddress",
  DocumentationConsentExtraIncome = "DocumentationConsentExtraIncome",
  DocumentationConsentIncomeDeviation = "DocumentationConsentIncomeDeviation",
  ApplePay = "ApplePay",
  GooglePay = "GooglePay",
  SamsungPay = "SamsungPay",
  Unknown = "Unknown",
  Purchase = "Purchase",
  AdditionalApplicationInfo = "AdditionalApplicationInfo",
  SignPrimaryApplicant = "SignPrimaryApplicant",
  SignCoApplicant = "SignCoApplicant",
  RepaymentPeriod = "RepaymentPeriod",
  CreditCardCrossSell = "CreditCardCrossSell",
  CreditCardCrossSellExperiment = "CreditCardCrossSellExperiment",
  CreditCardCrossSellAutomatic = "CreditCardCrossSellAutomatic",
  FormClickNext = "FormClickNext",
  FormClickPrevious = "FormClickPrevious",
  Received = "Received",
  Approved = "Approved",
  Rejected = "Rejected",
  CustomerStatement = "CustomerStatement",
  ModifyCreditLimit = "ModifyCreditLimit",
  Apply = "Apply",
  RefinanceRestrictionsApproved = "RefinanceRestrictionsApproved",
  Buffered = "Buffered",
  Error = "Error",
  ChooseCategory = "ChooseCategory",
  PpiPromotion = "PpiPromotion",
  ESkatConsent = "ESkatConsent",
  Copy = "Copy",
  ShowCardDetails = "ShowCardDetails",
  CardDetails = "CardDetails",
}

export enum EventActionValue {
  CrossSellID1 = "CrossSellID1",
  CrossSellID2 = "CrossSellID2",
  CrossSellAddOn = "CrossSellAddOn",
  CrossSellBanner = "CrossSellBanner",
  CreditCardCrossSellItem = "CreditCardCrossSellItem",
  Ppi = "Ppi",
}

export enum EventLabel {
  Edit = "Edit",
  Delete = "Delete",
  Add = "Add",
  Apply = "Apply",
  AddDept = "AddDept",
  ConfirmedEdit = "ConfirmedEdit",
  AccountNumberConfirmed = "AccountNumberConfirmed",
  AcceptCoApplicant = "AcceptCoApplicant",
  Completed = "Completed",
  CreditCard = "CreditCard",
  CreditLine = "CreditLine",
  SavingsAccount = "SavingsAccount",
  SavingsAccountPlus = "SavingsAccountPlus",
  SavingsAccountDelayedWithdrawal = "SavingsAccountDelayedWithdrawal",
  Loan = "Loan",
  SalesFinance = "SalesFinance",
  CashPoints = "CashPoints",
  Transactions = "Transactions",
  Payments = "Payments",
  Transfer = "Transfer",
  Invoice = "Invoice",
  Other = "Other",
  Alert = "Alert",
  Settings = "Settings",
  Pdf = "Pdf",
  Excel = "Excel",
  Viewed = "Viewed",
  Init = "Init",
  Approved = "Approved",
  Rejected = "Rejected",
  CreditCardCrossSell = "CreditCardCrossSell",
  CreditCardCrossSellOriginalDialog = "CreditCardCrossSellOriginalDialog",
  CreditCardCrossSellChallengerDialog = "CreditCardCrossSellChallengerDialog",
  CreditCardCrossSellExpanerDialog = "CreditCardCrossSellExpanderDialog",
  CreditCardCrossSellAfterSigningDialog = "CreditCardCrossSellAfterSigningDialog",
  NoGiConsentDialog = "NoGiConsentDialog",
  CardNumber = "CardNumber",
  CVV2 = "CVV2",
  ExpireDate = "ExpireDate",
  AccountNumber = "AccountNumber",
  Iban = "Iban",
  Bic = "Bic",
  Ocr = "Ocr",
  OcrGiroNumber = "OcrGiroNumber",
  BankName = "BankName",
  MandateId = "MandateId",
  InvoiceRecipientName = "InvoiceRecipientName",
  Started = "Started",
  Open = "Open",
}

export default BNGoogleTagManager;
